import {
  isStudioPricingReferrer,
  STUDIO_PRICING_WORKSPACE_GROUP_PROPERTY,
  STUDIO_PRICING_WORKSPACE_GROUP_PROPERTY_VALUE,
  STUDIO_WORKSPACE_GROUP_PROPERTY,
  STUDIO_WORKSPACE_GROUP_PROPERTY_VALUE,
} from 'src/constants/studioFtux';

/**
 * Workspace group properties for PostHog client `group()` / SSR `getAllFlags`.
 * Studio portals include the cohort trait so flags targeting `studio="true"`
 * evaluate before (or without relying on) async `$groupidentify` ingest.
 *
 * Both traits derive from persisted PortalConfig, so every dashboard load
 * re-asserts them: a `$groupidentify` the backend failed to deliver at workspace
 * creation is repaired here rather than leaving the workspace untargetable.
 */
export const getWorkspacePosthogGroupProperties = (
  portalName: string,
  isStudioUser: boolean,
  referrer?: string,
): Record<string, string> => ({
  name: portalName,
  ...getStudioGroupProperties(isStudioUser, referrer),
});

/** Options bag for posthog-node `getAllFlags` workspace evaluation. */
export const getPosthogGetAllFlagsOptions = (
  portalHeader: string,
  isStudioUser: boolean,
  referrer?: string,
): {
  groups: { workspace: string };
  groupProperties?: {
    workspace: Record<string, string>;
  };
} => {
  const studioProperties = getStudioGroupProperties(isStudioUser, referrer);
  return {
    groups: { workspace: portalHeader },
    ...(Object.keys(studioProperties).length > 0
      ? { groupProperties: { workspace: studioProperties } }
      : {}),
  };
};

/**
 * Cache key so Studio, Studio pricing and Classic evaluations never share a
 * cached result — the pricing cohort resolves flags the plain Studio cohort does
 * not, so it needs its own entry.
 */
export const getPosthogFlagsCacheKey = (
  portalHeader: string,
  isStudioUser: boolean,
  referrer?: string,
): string => {
  if (isStudioPricingReferrer(referrer)) {
    return `${portalHeader}:studio-pricing`;
  }
  return isStudioUser ? `${portalHeader}:studio` : portalHeader;
};

const getStudioGroupProperties = (
  isStudioUser: boolean,
  referrer?: string,
): Record<string, string> => ({
  ...(isStudioUser
    ? {
        [STUDIO_WORKSPACE_GROUP_PROPERTY]:
          STUDIO_WORKSPACE_GROUP_PROPERTY_VALUE,
      }
    : {}),
  ...(isStudioPricingReferrer(referrer)
    ? {
        [STUDIO_PRICING_WORKSPACE_GROUP_PROPERTY]:
          STUDIO_PRICING_WORKSPACE_GROUP_PROPERTY_VALUE,
      }
    : {}),
});
